/**
 * PreviewList — the "Simple list" / table-of-contents alternative to the
 * PreviewTile card grid, for courses whose layout is 'list'. Same gradient
 * shell as PreviewTileGrid so the two layouts feel like one system; each row
 * shows a number (or a small thumbnail when a header picture exists), the
 * title, and a chevron.
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon, LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { EASE_OUT } from '@/lib/motion';
import { useImageUrl } from '../../../services/uploads';

export function PreviewList({ children }: { children: React.ReactNode }) {
  return (
    <section
      className="relative rounded-[28px] p-[10px] sm:p-3"
      style={{
        background:
          'linear-gradient(to right, rgb(var(--color-primary) / 0.20), rgb(var(--color-secondary) / 0.20))',
      }}
    >
      <div className="rounded-[20px] bg-background-primary px-3 sm:px-5 py-4 sm:py-5 flex flex-col gap-2.5">
        {children}
      </div>
    </section>
  );
}

export type PreviewListItemState = 'open' | 'locked' | 'finished';

export interface PreviewListItemProps {
  /** S3 key or resolved URL of the header picture; null → numbered placeholder. */
  imageUrl?: string | null;
  /** Small label above the title, e.g. "Section 1". */
  kicker?: string;
  index: number;
  title: string;
  /** Learner gating. Defaults to 'open' (the trainer preview is always open). */
  state?: PreviewListItemState;
  /** Word used in the locked hint ("Finish the previous section"). */
  lockedScope?: string;
  onClick?: () => void;
  entranceDelay?: number;
}

export default function PreviewListItem({
  imageUrl,
  kicker,
  index,
  title,
  state = 'open',
  lockedScope = 'section',
  onClick,
  entranceDelay = 0,
}: PreviewListItemProps) {
  const reduceMotion = useReducedMotion();
  const [resolved] = useImageUrl(imageUrl ?? null);
  const locked = state === 'locked';

  return (
    <motion.button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      aria-disabled={locked}
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : entranceDelay }}
      className={`group flex items-center gap-4 w-full rounded-2xl border p-3 text-left transition-colors ${
        locked
          ? 'border-border-primary/15 bg-surface-primary/60 opacity-60 cursor-not-allowed'
          : 'border-border-primary/15 bg-surface-primary hover:border-primary-500/40 hover:bg-primary-500/[0.04] active:scale-[0.995]'
      }`}
    >
      {resolved ? (
        <img src={resolved} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
      ) : (
        <span className="w-14 h-14 rounded-xl bg-primary-500/10 text-primary-500 text-lg font-extrabold flex items-center justify-center shrink-0">
          {index + 1}
        </span>
      )}

      <span className="flex-1 min-w-0">
        {kicker && (
          <span className="block text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
            {kicker}
          </span>
        )}
        <span className="block text-sm font-bold text-text-primary truncate">{title}</span>
        {locked && (
          <span className="block text-[11px] text-text-tertiary truncate">
            Finish the previous {lockedScope} to unlock
          </span>
        )}
      </span>

      {state === 'finished' ? (
        <CheckCircleIcon className="w-5 h-5 shrink-0 text-success-500" />
      ) : locked ? (
        <LockClosedIcon className="w-5 h-5 shrink-0 text-text-tertiary" />
      ) : (
        <ChevronRightIcon className="w-5 h-5 shrink-0 text-text-tertiary group-hover:text-primary-500 transition-colors" />
      )}
    </motion.button>
  );
}
